import * as React from "react";
import DateBox from "devextreme-react/date-box";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Controller, RegisterOptions, UseFormReturn } from "react-hook-form";

import { useGlobalData } from "../../GlobalDataProvider";
import { getValueByString } from "../Utils";
import FieldError from "./FieldError";

interface IProps {
  label: string;
  fieldName: string;
  formHook: UseFormReturn;
  options?: RegisterOptions;
  children?: React.ReactNode;
  step?: number; // seconds; DevExtreme interval supports minutes (see mapping below)
  tooltipId?: string;
  isDisabled?: boolean;
  min?: number; // minutes since midnight (00:00 -> 0)
  max?: number; // minutes since midnight (23:59 -> 1439)
}

/* ---------- helpers ---------- */

const pad2 = (n: number) => n.toString().padStart(2, "0");

function minutesToDate(minutes: number, withSeconds = false): Date {
  const d = new Date();
  d.setHours(Math.floor(minutes / 60), minutes % 60, withSeconds ? 0 : 0, 0);
  return d;
}

function timeStringToDate(str?: string): Date | null {
  if (!str) return null;
  const m = str.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  const s = m[3] ? Number(m[3]) : 0;
  if (h > 23 || mi > 59 || s > 59) return null;
  const d = new Date();
  d.setHours(h, mi, s, 0);
  return d;
}

function dateToTimeString(d: Date): string {
  const h = pad2(d.getHours());
  const m = pad2(d.getMinutes());
  const s = pad2(d.getSeconds());
  return `${h}:${m}`;
}

const TimePickerEditor24: React.FC<IProps> = ({ label, fieldName, formHook, options, step, children, tooltipId, isDisabled, min, max }) => {
  const { control, formState } = formHook;
  const errors = formState.errors;
  const globalData = useGlobalData();

  const isInValid: any = getValueByString(errors, fieldName);

  // DevExtreme:
  // - type="time"
  // - displayFormat enforces 24h UI ("HH:mm" or "HH:mm:ss")
  // - interval is in minutes
  const displayFormat = "HH:mm";

  // Map min/max minutes -> Date
  const minDate = typeof min === "number" ? minutesToDate(min) : undefined;
  const maxDate = typeof max === "number" ? minutesToDate(max) : undefined;

  // Tooltips (unchanged)
  const renderTooltip = (props: any) => {
    if (tooltipId && globalData.data?.Tooltip.mapTooltip[tooltipId])
      return (
        <Tooltip {...props} className="defaultTooltip">
          <span style={{ whiteSpace: "pre-line" }}>{globalData.data?.Tooltip.mapTooltip[tooltipId]}</span>
        </Tooltip>
      );
    return <></>;
  };

  const inputCore = (
    <Controller
      name={fieldName}
      control={control}
      rules={options}
      render={({ field, fieldState }) => {
        const invalid = !!fieldState.error;

        // RHF stores "HH:MM(:SS)"; convert to Date for DateBox value
        const valueAsDate = (field.value && timeStringToDate(field.value)) || null;

        return (
          <>
            <OverlayTrigger placement="top" overlay={renderTooltip}>
              <div className="w-100">
                <DateBox
                  // 24h time-only
                  type="time"
                  value={valueAsDate}
                  onValueChanged={(e) => {
                    const v: Date | null = e.value ?? null;
                    if (v && v instanceof Date && !isNaN(v.getTime())) {
                      field.onChange(dateToTimeString(v));
                    } else {
                      field.onChange("");
                    }
                  }}
                  onClosed={() => field.onBlur()}
                  disabled={!!isDisabled}
                  // Look & feel
                  className={`dx-theme-material ${invalid ? "is-invalid" : ""}`}
                  inputAttr={{ class: `form-control ${invalid ? "is-invalid" : ""}`, id: label, "aria-invalid": invalid }}
                  stylingMode="outlined"
                  pickerType="rollers" // or "list" if you prefer
                  useMaskBehavior={true}
                  displayFormat={displayFormat} // forces 24h display
                  // Bounds & increments
                  min={minDate}
                  max={maxDate}
                  interval={1} // minutes
                  // Keep the value purely time; serialization stays local since we map to string
                  // applyValueMode="useButtons" // optional; shows OK/Cancel in popup
                />
              </div>
            </OverlayTrigger>

            {children}
          </>
        );
      }}
    />
  );

  // Keep your exact DOM structure and classes
  const wrappedCore = <div className={(children ? "input-group" : "") + " w-100"}>{inputCore}</div>;

  // Validation message between label and field
  const withLabel = label ? (
    <label className="form-label w-100">
      {label}
      <FieldError error={isInValid} />
      {wrappedCore}
    </label>
  ) : (
    <div className="w-100">
      <FieldError error={isInValid} />
      {wrappedCore}
    </div>
  );

  return <div className="input-group has-validation">{withLabel}</div>;
};

export default TimePickerEditor24;
